// Register a document (or take a number before the letter exists) in one of
// the project's journals, or correct an entry's card. The journal, number and
// date are fixed once given; the card — correspondent, subject, who signed,
// who handles it and by when — can be corrected later.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { FieldError, REQUIRED_LABEL } from '@/shared/ui/FieldError.jsx';
import { records } from '../lib/api.js';
import { fmtDay, todayInTashkent } from '../lib/journals.js';
import { LIMITS, registrationProblems } from '../lib/validation.js';

const FIELDS = [
  'register_id', 'reg_date', 'case_id', 'correspondent', 'signed_by', 'their_number', 'their_date',
  'subject', 'executor_user_id', 'due_date', 'note',
];

const NONE = 'none';
const stem = (name) => (name || '').replace(/\.[^.]+$/, '');
const personName = (p) => p?.display_name || p?.email || '';

function ExecutorPicker({ value, onChange }) {
  const { t } = useTranslation('records');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const res = await records.people(query.trim());
      setPeople(res.success ? res.response : []);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query, open]);

  if (value) {
    return (
      <div className="flex h-9 items-center justify-between rounded-md border px-3 text-sm" data-testid="executor-chosen">
        <span>{value.name}</span>
        <button type="button" aria-label={t('register.clearExecutor')} onClick={() => onChange(null)} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Input
        id="register-executor"
        value={query}
        placeholder={t('register.executorSearch')}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
      />
      {open && people && (
        <ul role="listbox" aria-label={t('register.executor')} className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
          {people.length === 0 && <li className="px-3 py-2 text-xs text-muted-foreground">{t('register.nobody')}</li>}
          {people.slice(0, 8).map((p) => (
            <li key={p.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={() => { onChange({ id: p.id, name: personName(p) }); setOpen(false); setQuery(''); }}
              >
                {personName(p)}
                {p.display_name && p.email && <span className="text-muted-foreground"> · {p.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function blankForm(file, registerId) {
  return {
    register_id: registerId ? String(registerId) : '',
    reg_date: todayInTashkent(),
    case_id: NONE,
    correspondent: '',
    subject: stem(file?.name),
    signed_by: '',
    executor: null,
    due_date: '',
    their_number: '',
    their_date: '',
    note: '',
  };
}

function formOf(entry) {
  return {
    register_id: String(entry.register_id),
    reg_date: entry.reg_date,
    case_id: entry.case_id ? String(entry.case_id) : NONE,
    correspondent: entry.correspondent || '',
    subject: entry.subject || '',
    signed_by: entry.signed_by || '',
    executor: entry.executor_user_id ? { id: entry.executor_user_id, name: entry.executor_name || '' } : null,
    due_date: entry.due_date || '',
    their_number: entry.their_number || '',
    their_date: entry.their_date || '',
    note: entry.note || '',
  };
}

export function RegisterDialog({ open, onOpenChange, file = null, entry = null, defaultRegisterId = null, onDone }) {
  const { t } = useTranslation('records');
  const [registers, setRegisters] = useState(null);
  const [existing, setExisting] = useState([]);
  const [cases, setCases] = useState([]);
  const [form, setForm] = useState(() => blankForm(file, defaultRegisterId));
  const [busy, setBusy] = useState(false);
  const editing = Boolean(entry);
  const today = todayInTashkent();
  const checks = useFieldErrors(registrationProblems(form, today));
  const { errors, reset: resetChecks } = checks;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setForm(entry ? formOf(entry) : blankForm(file, defaultRegisterId));
    resetChecks();
    records.registers().then((res) => {
      if (cancelled) return;
      const list = res.success ? res.response : [];
      setRegisters(list);
      if (!entry && !defaultRegisterId) {
        const first = list.find((r) => r.active);
        if (first) setForm((prev) => ({ ...prev, register_id: prev.register_id || String(first.id) }));
      }
    });
    if (file && !entry) {
      records.registrations({ file_id: file.id, status: 'registered' }).then((res) => {
        if (!cancelled) setExisting(res.success ? res.response.items : []);
      });
    } else {
      setExisting([]);
    }
    return () => {
      cancelled = true;
    };
  }, [open, file, entry, defaultRegisterId, resetChecks]);

  // The cases of the plan for the registration's year (draft or approved).
  const year = Number((form.reg_date || '').slice(0, 4));
  useEffect(() => {
    if (!open || !year) return undefined;
    let cancelled = false;
    records.plans().then(async (res) => {
      const plan = res.success ? res.response.find((p) => p.year === year && p.status !== 'closed') : null;
      const detail = plan ? await records.plan(plan.id) : null;
      if (!cancelled) setCases(detail?.success ? detail.response.items.filter((c) => c.status === 'open') : []);
    });
    return () => {
      cancelled = true;
    };
  }, [open, year]);

  const journal = useMemo(
    () => (registers || []).find((r) => String(r.id) === form.register_id) || null,
    [registers, form.register_id],
  );
  const set = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((k) => checks.touch(k === 'executor' ? 'executor_user_id' : k));
  };
  const describe = (field) => checks.describe(field, 'register');
  const caseMissing = journal?.needs_case && form.case_id === NONE;
  const caseLocked = editing && journal?.needs_case;
  const valid = form.register_id && form.reg_date && !caseMissing;

  const card = () => ({
    correspondent: form.correspondent.trim() || null,
    subject: form.subject.trim() || null,
    signed_by: form.signed_by.trim() || null,
    executor_user_id: form.executor?.id ?? null,
    due_date: form.due_date || null,
    their_number: form.their_number.trim() || null,
    their_date: form.their_date || null,
    note: form.note.trim() || null,
  });

  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    const caseId = form.case_id === NONE ? null : Number(form.case_id);
    const res = editing
      ? await records.updateRegistration(entry.id, { ...card(), ...(caseLocked ? {} : { case_id: caseId }) })
      : await records.register({
        register_id: Number(form.register_id), reg_date: form.reg_date, file_id: file?.id ?? null, case_id: caseId, ...card(),
      });
    setBusy(false);
    if (res.success) {
      toast.success(editing ? t('register.saved') : t('register.done', { number: res.response.number }));
      onDone?.(res.response);
      onOpenChange(false);
    } else {
      checks.showServer(res, FIELDS);
    }
  };

  const incoming = journal?.kind === 'incoming';

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('register.editTitle', { number: entry.number }) : t('register.title')}
          </DialogTitle>
          <DialogDescription>{file ? t('register.forFile', { name: file.name }) : t('register.description')}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
          {existing.length > 0 && (
            <p className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2 text-xs text-amber-800 dark:text-amber-300" data-testid="register-existing">
              {t('register.already', { list: existing.map((r) => `${r.register_name} № ${r.number} (${fmtDay(r.reg_date)})`).join('; ') })}
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
            <div className="space-y-1.5">
              <Label className={editing ? undefined : REQUIRED_LABEL}>{t('register.journal')}</Label>
              {editing ? (
                <Input value={`${entry.register_name} · № ${entry.number}`} disabled />
              ) : (
                <Select value={form.register_id} onValueChange={(v) => set({ register_id: v })}>
                  <SelectTrigger aria-label={t('register.journal')}><SelectValue placeholder={t('register.pickJournal')} /></SelectTrigger>
                  <SelectContent>
                    {(registers || []).filter((r) => r.active).map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!editing && journal && (
                <p className="text-xs text-muted-foreground" data-testid="register-next">{t('register.next', { number: journal.next_number })}</p>
              )}
              {!editing && registers && registers.filter((r) => r.active).length === 0 && (
                <p className="text-xs text-muted-foreground">{t('register.noJournals')}</p>
              )}
              <FieldError id="register-register_id-error" message={errors.register_id} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="register-date" className={editing ? undefined : REQUIRED_LABEL}>{t('register.date')}</Label>
              <Input id="register-date" type="date" required min={LIMITS.regDateMin} max={today} value={form.reg_date} disabled={editing}
                onChange={(e) => set({ reg_date: e.target.value })} {...describe('reg_date')} />
              <FieldError id="register-reg_date-error" message={errors.reg_date} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('register.case')}</Label>
            <Select value={form.case_id} onValueChange={(v) => set({ case_id: v })} disabled={caseLocked}>
              <SelectTrigger aria-label={t('register.case')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t('register.noCase')}</SelectItem>
                {cases.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.case_index} · {c.title}</SelectItem>)}
                {entry?.case_id && !cases.some((c) => c.id === entry.case_id) && (
                  <SelectItem value={String(entry.case_id)}>{entry.case_index}</SelectItem>
                )}
              </SelectContent>
            </Select>
            {caseMissing && <p className="text-xs text-amber-700 dark:text-amber-300">{t('register.needsCase')}</p>}
            <FieldError id="register-case_id-error" message={errors.case_id} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="register-correspondent">{t(incoming ? 'register.from' : 'register.correspondent')}</Label>
              <Input id="register-correspondent" maxLength={LIMITS.correspondent} value={form.correspondent}
                onChange={(e) => set({ correspondent: e.target.value })} {...describe('correspondent')} />
              <FieldError id="register-correspondent-error" message={errors.correspondent} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="register-signed">{t('register.signedBy')}</Label>
              <Input id="register-signed" maxLength={LIMITS.signedBy} value={form.signed_by}
                onChange={(e) => set({ signed_by: e.target.value })} {...describe('signed_by')} />
              <FieldError id="register-signed_by-error" message={errors.signed_by} />
            </div>
          </div>
          {incoming && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="register-their-number">{t('register.theirNumber')}</Label>
                <Input id="register-their-number" maxLength={LIMITS.theirNumber} value={form.their_number}
                  onChange={(e) => set({ their_number: e.target.value })} {...describe('their_number')} />
                <FieldError id="register-their_number-error" message={errors.their_number} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="register-their-date">{t('register.theirDate')}</Label>
                <Input id="register-their-date" type="date" min={LIMITS.dateMin} max={form.reg_date || today} value={form.their_date}
                  onChange={(e) => set({ their_date: e.target.value })} {...describe('their_date')} />
                <FieldError id="register-their_date-error" message={errors.their_date} />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="register-subject">{t('register.subject')}</Label>
            <Textarea id="register-subject" rows={2} maxLength={LIMITS.subject} value={form.subject}
              onChange={(e) => set({ subject: e.target.value })} {...describe('subject')} />
            <FieldError id="register-subject-error" message={errors.subject} />
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
            <div className="space-y-1.5">
              <Label htmlFor="register-executor">{t('register.executor')}</Label>
              <ExecutorPicker value={form.executor} onChange={(executor) => set({ executor })} />
              <FieldError id="register-executor_user_id-error" message={errors.executor_user_id} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="register-due">{t('register.due')}</Label>
              <Input id="register-due" type="date" min={form.reg_date || undefined} max={LIMITS.dateMax} value={form.due_date}
                onChange={(e) => set({ due_date: e.target.value })} {...describe('due_date')} />
              <FieldError id="register-due_date-error" message={errors.due_date} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="register-note">{t('register.note')}</Label>
            <Input id="register-note" maxLength={LIMITS.note} value={form.note}
              onChange={(e) => set({ note: e.target.value })} {...describe('note')} />
            <FieldError id="register-note-error" message={errors.note} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button onClick={submit} disabled={busy || !valid || checks.blocked} data-testid="register-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t(editing ? 'actions.save' : 'register.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
